import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { IShiftsService } from './IShiftsService';
import type { GetShiftRegisterQuery } from './queries';
import type {
    GetShiftLedgerSummaryResponse,
    GetShiftRegisterResponse,
    ShiftLedgerSummaryWire,
    ShiftRegisterPageWire,
} from './responses';
import { ShiftsEndpoints } from './shiftsEndpoints';
import { toShiftLedgerSummary, toShiftRegisterPage } from './shiftsMapping';

type ShiftsServiceOptions = {
    baseUrl: string;
};

export class ShiftsService implements IShiftsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: ShiftsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getRegister(
        query: GetShiftRegisterQuery,
    ): Promise<ApiResponse<GetShiftRegisterResponse>> {
        // An omitted filter must be *absent*, not blank: the server reads
        // `branchId=` as a filter on the empty string and answers with nothing
        // at all — an empty register that looks like a real one.
        const params = new URLSearchParams();
        if (query.branchId) {
            params.set('branchId', query.branchId);
        }
        if (query.from) {
            params.set('from', query.from);
        }
        if (query.to) {
            params.set('to', query.to);
        }
        if (query.status) {
            params.set('status', query.status);
        }
        params.set('page', String(query.page));
        params.set('pageSize', String(query.pageSize));

        const result = await makeApiRequest<ShiftRegisterPageWire>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                `${ShiftsEndpoints.register()}?${params.toString()}`,
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toShiftRegisterPage(result.response),
        };
    }

    public async getLedgerSummary(
        shiftId: string,
        locale?: string,
    ): Promise<ApiResponse<GetShiftLedgerSummaryResponse>> {
        const url = ShiftsEndpoints.ledgerSummary(shiftId);
        const result = await makeApiRequest<ShiftLedgerSummaryWire>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                locale ? `${url}?locale=${encodeURIComponent(locale)}` : url,
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toShiftLedgerSummary(result.response),
        };
    }
}
